import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NavBarLateral } from "../NavBarLateral";
import { IntegracionStepper } from "./IntegracionStepper";
import alertService from "../../../_services/cart/alert.service";
import authService from "../../../_services/cart/authService";
import facturacionService, { mensajeDeError } from "../../../_services/cart/facturacion.service";

/**
 * Facturacion electronica con ARCA (ex AFIP), como asistente paso a paso.
 *
 * Portada fiel de `shared/arca-integracion` de tienda-admin (13b5a5b): mismos ocho pasos, textos, capturas,
 * validaciones y mensajes. Cada paso tiene su ayuda con capturas del sitio de ARCA y, cuando hay que hacer
 * algo en nuestra app (cargar datos, generar la solicitud, subir el certificado, el punto de venta), el
 * formulario esta en ese mismo paso. El ultimo paso prueba la conexion de verdad contra ARCA y, si algo
 * falla, manda al paso que hay que revisar.
 *
 * Diferencias con tienda-admin: la facturacion queda activa recien cuando la prueba da OK (alla se activaba
 * al subir el certificado), se suma "facturar automaticamente mis ventas", y sin la aprobacion por Telegram.
 */

const BASE_PATH = "/ayuda/arca/";
const CONDICIONES_IVA = ["Responsable Inscripto", "Monotributista", "Exento", "No Responsable", "Consumidor Final"];

const exito = (texto) => alertService.msg(texto, "success");
const error = (err, porDefecto) => alertService.msg(mensajeDeError(err, porDefecto), "error");

export const ArcaIntegracion = () => {
    const stepper = useRef(null);
    const [cargando, setCargando] = useState(true);
    const [paso, setPaso] = useState(0);

    const [form, setForm] = useState({ cuit: "", razonSocial: "", condicionIva: "", domicilioFiscal: "", puntoVenta: "", certPassword: "" });
    const [tocado, setTocado] = useState(false);

    // Lo que ya esta guardado en el backend (no lo que se esta escribiendo): es lo que desbloquea los pasos.
    const [datosGuardados, setDatosGuardados] = useState(false);
    const [certificadoSubido, setCertificadoSubido] = useState(false);
    const [puntoVentaGuardado, setPuntoVentaGuardado] = useState(null);
    const [ambienteProd, setAmbienteProd] = useState(false);
    const [habilitado, setHabilitado] = useState(false);

    const [guardandoDatos, setGuardandoDatos] = useState(false);
    const [generandoCsr, setGenerandoCsr] = useState(false);
    const [crtFile, setCrtFile] = useState(null);
    const [subiendoCrt, setSubiendoCrt] = useState(false);
    // Alternativa al CSR: un .p12 ya emitido para el mismo CUIT (p.ej. en otro sistema).
    const [mostrarP12, setMostrarP12] = useState(false);
    const [p12File, setP12File] = useState(null);
    const [p12Password, setP12Password] = useState("");
    const [subiendoP12, setSubiendoP12] = useState(false);
    const [guardandoPuntoVenta, setGuardandoPuntoVenta] = useState(false);

    const [probando, setProbando] = useState(false);
    const [prueba, setPrueba] = useState(null);

    const [autoVentas, setAutoVentas] = useState(false);
    const [guardandoAutoVentas, setGuardandoAutoVentas] = useState(false);

    // Solo el admin de la plataforma.
    const esAdminGlobal = authService.hasAuthority("ADMIN");
    const [autoSuscripciones, setAutoSuscripciones] = useState(false);
    const [guardandoAutoSuscripciones, setGuardandoAutoSuscripciones] = useState(false);

    const set = (campo) => (e) => setForm({ ...form, [campo]: e.target.value });
    const cuitValido = /^\d{11}$/.test(form.cuit);
    const razonValida = form.razonSocial.trim().length > 0 && form.razonSocial.length <= 200;

    const configurado = datosGuardados && certificadoSubido && !!puntoVentaGuardado && ambienteProd;

    const pasos = [
        {
            id: "datos",
            titulo: "Datos fiscales",
            ayuda: "Estos datos salen impresos en cada factura. Usá los mismos que figuran en ARCA para tu CUIT. Para todo el trámite vas a necesitar tu <strong>clave fiscal nivel 3</strong>.",
            bloqueo: () => datosGuardados ? null : "Guardá los datos fiscales para seguir",
        },
        {
            id: "solicitud",
            titulo: "Generar solicitud",
            ayuda: "ARCA no te da el certificado directamente: primero hay que pedírselo con un <strong>archivo de solicitud</strong> (CSR) que generamos acá con tu CUIT y razón social. Descargalo y tenelo a mano: lo vas a subir en ARCA en el paso 4.",
        },
        {
            id: "ingresar",
            titulo: "Entrar a ARCA",
            ayuda: "Entrá a ARCA con tu CUIT y clave fiscal. Todo lo que sigue se hace desde el buscador del portal (<em>\"¿Qué necesitás?\"</em>).",
            link: { url: "https://auth.afip.gob.ar/contribuyente_/login.xhtml", texto: "Abrir ARCA" },
            capturas: [
                { src: "01-login.jpg", texto: "Ingresá con tu CUIT y clave fiscal." },
                { src: "02-portal.jpg", texto: "Este es el portal. Los servicios que vamos a usar se buscan desde el buscador." },
            ],
        },
        {
            id: "alias",
            titulo: "Crear el certificado",
            ayuda: "En el buscador escribí <strong>\"certificados digitales\"</strong> y entrá a <strong>Administración de Certificados Digitales</strong>. Tocá <strong>Agregar alias</strong>, poné un nombre (es interno, por ejemplo <code>facturacion-web</code>), en <strong>Seleccionar archivo</strong> elegí la solicitud que descargaste en el paso 2 y confirmá con <strong>Agregar alias</strong>.",
            capturas: [
                { src: "03-buscar-certificados.jpg", texto: "Buscá \"certificados digitales\"." },
                { src: "04-certificados.jpg", texto: "Tus alias (si ya tenés alguno, aparece en la lista). Tocá \"Agregar alias\"." },
                { src: "05-agregar-alias.jpg", texto: "Nombre del alias + el archivo de solicitud del paso 2." },
            ],
        },
        {
            id: "certificado",
            titulo: "Subir el certificado",
            ayuda: "De vuelta en la lista, tocá <strong>Ver</strong> en el alias que creaste y descargá el certificado con el ícono de la columna <strong>Descargar</strong>. Es un archivo <code>.crt</code>: subilo acá abajo.",
            capturas: [
                { src: "06-descargar-crt.jpg", texto: "Detalle del alias: el ícono de \"Descargar\" baja el .crt." },
            ],
            bloqueo: () => certificadoSubido ? null : "Subí el certificado para seguir",
        },
        {
            id: "relacion",
            titulo: "Autorizar facturación",
            ayuda: "Ahora hay que autorizar a ese certificado a facturar. Buscá <strong>Administrador de Relaciones de Clave Fiscal</strong> y tocá <strong>Nueva Relación</strong>. En <strong>Servicio</strong> tocá <strong>Buscar</strong> → <strong>ARCA</strong> → <strong>WebServices</strong> → <strong>Facturación Electrónica</strong>. En <strong>Representante</strong> tocá <strong>Buscar</strong>, elegí en <strong>Computador Fiscal</strong> el alias del paso 4 y confirmá las dos pantallas que siguen.",
            capturas: [
                { src: "07-relaciones.jpg", texto: "Administrador de Relaciones: \"Nueva Relación\"." },
                { src: "09-servicio-arca.jpg", texto: "Servicio → Buscar: abrí ARCA y después WebServices." },
                { src: "10-facturacion-electronica.jpg", texto: "Elegí \"Facturación Electrónica\"." },
                { src: "12-computador-fiscal.jpg", texto: "Representante → Buscar: en \"Computador Fiscal\" elegí tu alias y confirmá." },
            ],
        },
        {
            id: "punto-venta",
            titulo: "Punto de venta",
            ayuda: "Las facturas por sistema necesitan un punto de venta propio. Buscá <strong>Administración de puntos de venta y domicilios</strong>, elegí tu CUIT, entrá a <strong>A/B/M de puntos de venta</strong> y tocá <strong>Agregar</strong>. En <strong>Sistema</strong> elegí <strong>Factura Electrónica - Monotributo - Web Services</strong> (si sos Responsable Inscripto: <strong>RECE para aplicativo y web services</strong>), completá número, nombre de fantasía y domicilio, y aceptá. Después cargá acá el <strong>mismo número</strong>: si no coinciden, ARCA rechaza las facturas.",
            capturas: [
                { src: "13-buscar-puntos-venta.jpg", texto: "Buscá \"puntos de venta\"." },
                { src: "15-pve-menu.jpg", texto: "Elegí tu CUIT y entrá a \"A/B/M de puntos de venta\"." },
                { src: "16-pve-listado.jpg", texto: "Tus puntos de venta. Marcado: uno de tipo Web Services, que es el que sirve." },
                { src: "17-pve-alta.jpg", texto: "\"Agregar\": el sistema tiene que ser el de Web Services." },
            ],
            bloqueo: () => puntoVentaGuardado ? null : "Guardá el número de punto de venta para seguir",
        },
        {
            id: "probar",
            titulo: "Activar y probar",
            ayuda: "Último paso: activamos la facturación y hacemos una prueba real contra ARCA, sin emitir nada. Las facturas que emitas después son <strong>legalmente válidas e irreversibles</strong>.",
        },
    ];

    const aplicarEstado = (e) => {
        setDatosGuardados(!!(e.cuit && e.razonSocial));
        setCertificadoSubido(!!e.afipCertificadoSubido);
        setPuntoVentaGuardado(e.puntoVenta || null);
        setAmbienteProd(e.afipAmbiente === "PROD");
        setHabilitado(!!e.afipHabilitado);
        setAutoVentas(!!e.facturacionAutoVentas);
        setAutoSuscripciones(!!e.facturacionAutoSuscripciones);
    };

    /** Arranca en el primer paso pendiente; si ya esta todo, en el de la prueba. */
    const pasoInicial = (e) => {
        const id = !(e.cuit && e.razonSocial) ? "datos"
            : !e.afipCertificadoSubido ? "solicitud"
                : !e.puntoVenta ? "punto-venta"
                    : "probar";
        return pasos.findIndex((p) => p.id === id);
    };

    useEffect(() => {
        facturacionService.estadoArca()
            .then((e) => {
                setForm((f) => ({
                    ...f,
                    cuit: e.cuit || "",
                    razonSocial: e.razonSocial || "",
                    condicionIva: e.condicionIva || "",
                    domicilioFiscal: e.domicilioFiscal || "",
                    puntoVenta: e.puntoVenta || "",
                }));
                aplicarEstado(e);
                setPaso(pasoInicial(e));
            })
            .catch((err) => error(err, "No se pudo cargar la configuración de ARCA"))
            .finally(() => setCargando(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Paso 1: datos fiscales ──

    const guardarDatos = () => {
        if (!cuitValido || !razonValida) {
            setTocado(true);
            return;
        }
        const { cuit, razonSocial, condicionIva, domicilioFiscal } = form;
        setGuardandoDatos(true);
        facturacionService.actualizarArca({ cuit, razonSocial, condicionIva, domicilioFiscal })
            .then((e) => {
                aplicarEstado(e);
                setDatosGuardados(true);
                exito("Datos fiscales guardados");
                setTimeout(() => stepper.current && stepper.current.siguiente());
            })
            .catch((err) => error(err, "Error al guardar los datos fiscales"))
            .finally(() => setGuardandoDatos(false));
    };

    // ── Paso 2: solicitud (CSR) ──

    const generarCsr = () => {
        setGenerandoCsr(true);
        facturacionService.generarCsr()
            .then(() => exito("Solicitud descargada. La vas a subir en ARCA en el paso 4."))
            .catch((err) => error(err, "Error generando la solicitud"))
            .finally(() => setGenerandoCsr(false));
    };

    // ── Paso 5: certificado ──

    const subirCrt = () => {
        if (!crtFile) { error(null, "Seleccioná el archivo .crt"); return; }
        if (!form.certPassword) { error(null, "Elegí una contraseña para proteger el certificado"); return; }
        setSubiendoCrt(true);
        facturacionService.subirCertificadoFirmado(crtFile, form.certPassword)
            .then((e) => {
                aplicarEstado(e);
                setCertificadoSubido(true);
                setCrtFile(null);
                setForm((f) => ({ ...f, certPassword: "" }));
                setPrueba(null);
                exito("Certificado cargado");
                setTimeout(() => stepper.current && stepper.current.siguiente());
            })
            .catch((err) => error(err, "Error al procesar el certificado"))
            .finally(() => setSubiendoCrt(false));
    };

    /**
     * Atajo para quien ya tiene un .p12 (certificado + clave privada) del MISMO CUIT, emitido en otro sistema:
     * se saltea la solicitud y el alias. No sirve con el .crt suelto: sin la clave con la que se pidio, no firma.
     */
    const subirP12 = () => {
        if (!p12File) { error(null, "Seleccioná el archivo .p12"); return; }
        if (!p12Password) { error(null, "Ingresá la contraseña del .p12"); return; }
        setSubiendoP12(true);
        facturacionService.subirCertificado(p12File, p12Password)
            .then((e) => {
                aplicarEstado(e);
                setCertificadoSubido(true);
                setP12File(null);
                setP12Password("");
                setPrueba(null);
                exito("Certificado cargado");
                setTimeout(() => stepper.current && stepper.current.siguiente());
            })
            .catch((err) => error(err, "Error al subir el certificado"))
            .finally(() => setSubiendoP12(false));
    };

    // ── Paso 7: punto de venta ──

    const guardarPuntoVenta = () => {
        const puntoVenta = Number(form.puntoVenta);
        if (!puntoVenta || puntoVenta < 1 || puntoVenta > 99999) {
            error(null, "Ingresá el número de punto de venta que creaste en ARCA");
            return;
        }
        setGuardandoPuntoVenta(true);
        facturacionService.actualizarArca({ puntoVenta })
            .then((e) => {
                aplicarEstado(e);
                setPuntoVentaGuardado(puntoVenta);
                exito("Punto de venta guardado");
                setTimeout(() => stepper.current && stepper.current.siguiente());
            })
            .catch((err) => error(err, "Error al guardar el punto de venta"))
            .finally(() => setGuardandoPuntoVenta(false));
    };

    // ── Paso 8: activar y probar ──

    const probar = () => {
        setProbando(true);
        setPrueba(null);
        facturacionService.probarConexion()
            .then((r) => {
                setPrueba(r);
                setHabilitado(!!r.ok);
            })
            .catch(() => error(null, "No se pudo hacer la prueba. Probá de nuevo en un rato."))
            .finally(() => setProbando(false));
    };

    /** Pasa la cuenta a produccion (si no lo estaba) y prueba la conexion real contra ARCA. */
    const activarYProbar = () => {
        if (ambienteProd) { probar(); return; }
        setProbando(true);
        facturacionService.actualizarArca({ afipAmbiente: "PROD" })
            .then((e) => {
                aplicarEstado(e);
                probar();
            })
            .catch((err) => {
                setProbando(false);
                error(err, "No se pudo activar la facturación");
            });
    };

    /** A que paso mandar al usuario segun en que etapa fallo la prueba. */
    const pasoParaRevisar = (etapa) => {
        switch (etapa) {
            case "DATOS": return [{ id: "datos", texto: "Revisar datos fiscales" }];
            case "CERTIFICADO_O_RELACION": return [
                { id: "certificado", texto: "Revisar certificado" },
                { id: "relacion", texto: "Revisar autorización" },
            ];
            case "PUNTO_DE_VENTA": return [{ id: "punto-venta", texto: "Revisar punto de venta" }];
            default: return [];
        }
    };

    // ── Opciones ──

    const guardarAutoVentas = (valor) => {
        setGuardandoAutoVentas(true);
        facturacionService.actualizarArca({ facturacionAutoVentas: valor })
            .then(() => {
                setAutoVentas(valor);
                exito(valor
                    ? "Cada venta que Mercado Pago confirme como pagada se va a facturar sola"
                    : "Facturación automática de ventas desactivada");
            })
            .catch((err) => error(err, "No se pudo guardar la facturación automática"))
            .finally(() => setGuardandoAutoVentas(false));
    };

    const guardarAutoSuscripciones = (valor) => {
        setGuardandoAutoSuscripciones(true);
        facturacionService.actualizarArca({ facturacionAutoSuscripciones: valor })
            .then(() => {
                setAutoSuscripciones(valor);
                exito(valor
                    ? "Los pagos de suscripción se van a facturar automáticamente con esta cuenta"
                    : "Facturación automática desactivada");
            })
            .catch((err) => error(err, "No se pudo guardar la facturación automática"))
            .finally(() => setGuardandoAutoSuscripciones(false));
    };

    const volverAHomologacion = () => {
        facturacionService.actualizarArca({ afipAmbiente: "HOMO" })
            .then((e) => {
                aplicarEstado(e);
                setPrueba(null);
                exito("Ambiente de homologación: las facturas no tienen validez fiscal");
            })
            .catch((err) => error(err, "No se pudo cambiar el ambiente"));
    };

    // ── Acciones de cada paso (los <ng-template pasoAccion> de tienda-admin) ──

    const acciones = {
        datos: (
            <div className="arca__grid">
                <label className="arca__campo">
                    <span className="cuenta-usuarios__label">CUIT (sin guiones) <span className="arca__requerido">*</span></span>
                    <input type="text" value={form.cuit} onChange={set("cuit")} placeholder="20123456789" maxLength={11} />
                    {tocado && !cuitValido && <span className="arca__error">Tiene que tener 11 dígitos</span>}
                </label>
                <label className="arca__campo">
                    <span className="cuenta-usuarios__label">Condición frente al IVA</span>
                    <select className="arca__select" value={form.condicionIva} onChange={set("condicionIva")}>
                        <option value="">Seleccionar…</option>
                        {CONDICIONES_IVA.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </label>
                <label className="arca__campo arca__campo--ancho">
                    <span className="cuenta-usuarios__label">Razón social <span className="arca__requerido">*</span></span>
                    <input type="text" value={form.razonSocial} onChange={set("razonSocial")} placeholder="Como figura en ARCA" />
                    {tocado && !razonValida && <span className="arca__error">La razón social es obligatoria</span>}
                </label>
                <label className="arca__campo arca__campo--ancho">
                    <span className="cuenta-usuarios__label">Domicilio fiscal</span>
                    <input type="text" value={form.domicilioFiscal} onChange={set("domicilioFiscal")} placeholder="Av. Corrientes 1234, CABA" maxLength={500} />
                </label>
                <div className="arca__campo--ancho arca__acciones">
                    <button type="button" className="button" data-type="accent" onClick={guardarDatos} disabled={guardandoDatos}>
                        {guardandoDatos ? "Guardando…" : "Guardar datos"}
                    </button>
                </div>
            </div>
        ),
        solicitud: (
            <div className="arca__pila">
                {certificadoSubido && (
                    <div className="arca__aviso arca__aviso--warn">
                        <FontAwesomeIcon icon="fa-solid fa-triangle-exclamation" />
                        <p>Ya tenés un certificado cargado. Generá una solicitud nueva <strong>solo</strong> si vas a hacer el trámite de nuevo (por ejemplo, porque venció): si no, salteá este paso.</p>
                    </div>
                )}
                <div className="arca__fila">
                    <p className="arca__nota">Se descarga <code>solicitud-arca.csr</code>.</p>
                    <button type="button" className="button" data-type="accent" onClick={generarCsr} disabled={generandoCsr}>
                        <FontAwesomeIcon icon="fa-solid fa-download" />
                        {generandoCsr ? "Generando…" : "Generar y descargar solicitud"}
                    </button>
                </div>
            </div>
        ),
        certificado: (
            <div className="arca__pila">
                {certificadoSubido && (
                    <div className="arca__aviso arca__aviso--ok">
                        <FontAwesomeIcon icon="fa-solid fa-circle-check" />
                        <p>Ya hay un certificado cargado. Podés reemplazarlo subiendo otro.</p>
                    </div>
                )}
                <label className="arca__archivo">
                    <FontAwesomeIcon icon="fa-solid fa-paperclip" />
                    <span>{crtFile ? crtFile.name : "Elegir el certificado (.crt)"}</span>
                    <input type="file" accept=".crt,.cer,.pem" onChange={(e) => e.target.files.length && setCrtFile(e.target.files[0])} hidden />
                </label>
                <label className="arca__campo">
                    <span className="cuenta-usuarios__label">Contraseña para protegerlo</span>
                    <input type="password" autoComplete="new-password" value={form.certPassword} onChange={set("certPassword")} placeholder="Elegí una" />
                    <span className="arca__nota">Queda guardada cifrada y se usa para firmar las facturas. No hace falta recordarla.</span>
                </label>
                <div className="arca__acciones">
                    <button type="button" className="button" data-type="accent" onClick={subirCrt} disabled={subiendoCrt}>
                        <FontAwesomeIcon icon="fa-solid fa-cloud-arrow-up" />
                        {subiendoCrt ? "Procesando…" : "Subir certificado"}
                    </button>
                </div>

                {/* Atajo: ya hay un .p12 del mismo CUIT emitido en otro sistema */}
                <div className="arca__separador">
                    <button type="button" className="arca__link" onClick={() => setMostrarP12(!mostrarP12)}>
                        <FontAwesomeIcon icon={mostrarP12 ? "fa-solid fa-chevron-up" : "fa-solid fa-chevron-down"} />
                        Ya tengo un certificado .p12 de este CUIT (de otro sistema)
                    </button>
                    {mostrarP12 && (
                        <div className="arca__pila arca__p12">
                            <p className="arca__nota">
                                Si ya hiciste el trámite en otro sistema, podés reusar ese certificado y saltearte los pasos 2 a 5.
                                Tiene que ser un <strong>.p12</strong> (certificado + clave privada): el .crt suelto no alcanza.
                            </p>
                            <label className="arca__archivo">
                                <FontAwesomeIcon icon="fa-solid fa-paperclip" />
                                <span>{p12File ? p12File.name : "Elegir archivo (.p12 o .pfx)"}</span>
                                <input type="file" accept=".p12,.pfx" onChange={(e) => e.target.files.length && setP12File(e.target.files[0])} hidden />
                            </label>
                            <label className="arca__campo">
                                <span className="cuenta-usuarios__label">Contraseña del .p12</span>
                                <input type="password" autoComplete="off" value={p12Password} onChange={(e) => setP12Password(e.target.value)} placeholder="La del archivo, no una nueva" />
                            </label>
                            <div className="arca__acciones">
                                <button type="button" className="button" data-type="secondary" onClick={subirP12} disabled={subiendoP12}>
                                    {subiendoP12 ? "Validando…" : "Usar este certificado"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        ),
        "punto-venta": (
            <div className="arca__fila arca__fila--abajo">
                <label className="arca__campo">
                    <span className="cuenta-usuarios__label">Número de punto de venta</span>
                    <input type="number" min="1" max="99999" className="arca__pv" value={form.puntoVenta} onChange={set("puntoVenta")} placeholder="Ej. 3" />
                </label>
                <button type="button" className="button" data-type="accent" onClick={guardarPuntoVenta} disabled={guardandoPuntoVenta}>
                    {guardandoPuntoVenta ? "Guardando…" : "Guardar"}
                </button>
                <p className="arca__nota arca__campo--ancho">
                    El mismo número que le pusiste en ARCA al punto de venta de tipo <strong>Web Services</strong>.
                </p>
            </div>
        ),
        probar: (
            <div className="arca__pila">
                <div className="arca__fila">
                    <p className="arca__nota">
                        {!ambienteProd && "Activa la facturación real y prueba la conexión."}
                        {ambienteProd && "La facturación está activa. Podés volver a probar cuando quieras."}
                    </p>
                    <div className="arca__fila">
                        {/* Solo el admin: probar contra los servidores de homologacion, sin pasar a produccion. */}
                        {esAdminGlobal && !ambienteProd && (
                            <button type="button" className="button" data-type="secondary" onClick={probar} disabled={probando}>
                                <FontAwesomeIcon icon="fa-solid fa-flask" /> Probar en homologación
                            </button>
                        )}
                        <button type="button" className="button" data-type="accent" onClick={activarYProbar} disabled={probando}>
                            <FontAwesomeIcon icon={probando ? "fa-solid fa-spinner" : "fa-solid fa-bolt"} spin={probando} />
                            {probando ? "Probando con ARCA…" : (ambienteProd ? "Probar conexión" : "Activar y probar")}
                        </button>
                    </div>
                </div>

                {prueba && prueba.ok && (
                    <div className="arca__aviso arca__aviso--ok">
                        <FontAwesomeIcon icon="fa-solid fa-circle-check" />
                        <div>
                            <p><strong>¡Conexión con ARCA funcionando!</strong></p>
                            <p>
                                Punto de venta {prueba.puntoVenta}: el último comprobante autorizado es el{" "}
                                <strong>{prueba.ultimoNumero}</strong>, así que la próxima factura sale con el{" "}
                                {(prueba.ultimoNumero || 0) + 1}. Ya podés facturar.
                            </p>
                        </div>
                    </div>
                )}

                {prueba && !prueba.ok && (
                    <div className="arca__aviso arca__aviso--error">
                        <FontAwesomeIcon icon="fa-solid fa-circle-exclamation" />
                        <div className="arca__pila arca__pila--chica">
                            <p><strong>
                                {prueba.etapa === "DATOS" && "Falta completar algo acá."}
                                {prueba.etapa === "CERTIFICADO_O_RELACION" && "ARCA rechazó el certificado o la autorización."}
                                {prueba.etapa === "PUNTO_DE_VENTA" && "ARCA no acepta ese punto de venta."}
                            </strong></p>
                            {prueba.etapa === "CERTIFICADO_O_RELACION" && (
                                <p>Lo más común: falta la relación con <strong>Facturación Electrónica</strong> (paso 6) o el certificado es de otro CUIT o venció (paso 5).</p>
                            )}
                            {prueba.etapa === "PUNTO_DE_VENTA" && (
                                <p>Revisá que exista, que sea de tipo <strong>Web Services</strong> y que el número coincida.</p>
                            )}
                            <p className="arca__respuesta">ARCA respondió: {prueba.mensaje}</p>
                            <div className="arca__fila arca__fila--inicio">
                                {pasoParaRevisar(prueba.etapa).map((r) => (
                                    <button key={r.id} type="button" className="arca__revisar" onClick={() => stepper.current && stepper.current.irAPaso(r.id)}>
                                        {r.texto}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        ),
    };

    // Configurado = datos completos. Si la ultima prueba contra ARCA fallo, eso pesa mas.
    const estadoPill = () => {
        if (cargando) return null;
        if (habilitado && !(prueba && !prueba.ok)) {
            return <span className="arca__pill arca__pill--ok"><span /> {ambienteProd ? "Conectado" : "Conectado (homologación)"}</span>;
        }
        if ((configurado || certificadoSubido) && prueba && !prueba.ok) {
            return <span className="arca__pill arca__pill--error"><span /> Revisar conexión</span>;
        }
        if (configurado) return <span className="arca__pill arca__pill--warn"><span /> Falta probar la conexión</span>;
        return <span className="arca__pill"><span /> Sin configurar</span>;
    };

    return (
        <div className="dashboard">
            <NavBarLateral handleClose={() => {}} />

            <div className="dashboard__content cuenta-usuarios">
                <div className="dashboard__content-header cuenta-usuarios__header">
                    <div className="dashboard__content-title">
                        <p className="cuenta-usuarios__eyebrow">MI CUENTA</p>
                        <h2>Facturación electrónica ARCA</h2>
                        <p className="panel__bajada">Conectá tu CUIT con ARCA (ex AFIP) para emitir facturas desde el sistema.</p>
                    </div>
                    {estadoPill()}
                </div>

                <div className="cuenta-usuarios__card">
                    {cargando && <p className="panel__estado-carga">Cargando…</p>}
                    {!cargando && (
                        <IntegracionStepper
                            ref={stepper}
                            pasos={pasos}
                            basePath={BASE_PATH}
                            paso={paso}
                            onPaso={setPaso}
                            acciones={acciones}
                            storageKey="arca"
                        />
                    )}
                </div>

                {/* Lo que se puede hacer recien con la integracion andando */}
                {!cargando && habilitado && (
                    <div className="cuenta-usuarios__card arca__opciones">
                        <p className="cuenta-usuarios__eyebrow">OPCIONES DE FACTURACIÓN</p>
                        <label className="arca__check">
                            <input type="checkbox" checked={autoVentas} disabled={guardandoAutoVentas} onChange={() => guardarAutoVentas(!autoVentas)} />
                            <span>
                                <strong>Facturar automáticamente mis ventas</strong>
                                <span className="arca__nota">
                                    Cuando Mercado Pago confirma el pago de una venta, se emite la factura a consumidor final y se le manda
                                    por mail al comprador. Las ventas por transferencia o efectivo se facturan a mano desde el detalle.
                                </span>
                            </span>
                        </label>
                    </div>
                )}

                {/* Solo el admin de la plataforma: con que cuenta salen las facturas de las cuotas */}
                {!cargando && esAdminGlobal && (
                    <div className="cuenta-usuarios__card arca__opciones">
                        <p className="cuenta-usuarios__eyebrow">OPCIONES DE LA PLATAFORMA</p>
                        <label className="arca__check">
                            <input type="checkbox" checked={autoSuscripciones} disabled={guardandoAutoSuscripciones} onChange={() => guardarAutoSuscripciones(!autoSuscripciones)} />
                            <span>
                                <strong>Facturar automáticamente los pagos de suscripción</strong>
                                <span className="arca__nota">
                                    Cuando Mercado Pago avisa un cobro de suscripción, se emite la factura con el CUIT de ESTA cuenta y se
                                    le manda por mail al cliente. Tiene que estar activada en una sola cuenta.
                                </span>
                            </span>
                        </label>
                        {ambienteProd && (
                            <button type="button" className="arca__link" onClick={volverAHomologacion}>
                                <FontAwesomeIcon icon="fa-solid fa-flask" /> Pasar a homologación (para probar sin validez fiscal)
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
